/**
 * Pagina dettaglio fornitore del modulo fornitori.
 *
 * Mostra i dati anagrafici del fornitore con due tab:
 * - Catalogo: voci catalogo prodotti
 * - Rappresentanti: contatti commerciali
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CatalogoTable from '../components/CatalogoTable';
import RappresentantiTable from '../components/RappresentantiTable';

interface FornitoreData {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  codice_fiscale: string | null;
  partita_iva: string | null;
  email: string | null;
  telefono: string | null;
  pec: string | null;
  codice_sdi: string | null;
  indirizzo: string | null;
  civico: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  nazione: string | null;
  stato: string;
  created_at: string;
  updated_at: string;
  catalogo_count: number;
  rappresentanti_count: number;
}

/**
 * Pagina dettaglio fornitore con tab catalogo e rappresentanti.
 */
const FornitoreDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [fornitore, setFornitore] = useState<FornitoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const caricaFornitore = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/fornitori/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: FornitoreData = await response.json();
      setFornitore(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void caricaFornitore();
  }, [caricaFornitore]);

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  if (error || !fornitore) {
    return (
      <p className="p-6 text-center text-sm text-destructive">
        {error ?? 'Fornitore non trovato'}
      </p>
    );
  }

  /** Nome visualizzabile del fornitore. */
  const displayName =
    fornitore.ragione_sociale ??
    [fornitore.cognome, fornitore.nome].filter(Boolean).join(' ') ??
    '—';

  return (
    <div className="space-y-6 p-6">
      {/* Intestazione */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          {displayName}
        </h1>
        <Badge
          variant={fornitore.stato === 'attivo' ? 'default' : 'secondary'}
        >
          {fornitore.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
        </Badge>
        <div className="ml-auto">
          <Button
            variant="outline"
            onClick={() => navigate(`/fornitori/${fornitore.id}/modifica`)}
          >
            Modifica
          </Button>
        </div>
      </div>

      {/* Dati anagrafici */}
      <Card>
        <CardHeader>
          <CardTitle>Dati anagrafici</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            {fornitore.ragione_sociale && (
              <div>
                <p className="text-muted-foreground">Ragione sociale</p>
                <p className="font-medium">{fornitore.ragione_sociale}</p>
              </div>
            )}
            {fornitore.nome && (
              <div>
                <p className="text-muted-foreground">Nome</p>
                <p className="font-medium">{fornitore.nome}</p>
              </div>
            )}
            {fornitore.cognome && (
              <div>
                <p className="text-muted-foreground">Cognome</p>
                <p className="font-medium">{fornitore.cognome}</p>
              </div>
            )}
            <div>
              <p className="text-muted-foreground">P.IVA</p>
              <p className="font-mono">{fornitore.partita_iva ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Codice fiscale</p>
              <p className="font-mono">{fornitore.codice_fiscale ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Codice SDI</p>
              <p className="font-mono">{fornitore.codice_sdi ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Email</p>
              <p>{fornitore.email ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">PEC</p>
              <p>{fornitore.pec ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Telefono</p>
              <p>{fornitore.telefono ?? '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-muted-foreground">Indirizzo</p>
              <p className="font-medium">
                {[fornitore.indirizzo, fornitore.civico]
                  .filter(Boolean)
                  .join(', ') || '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">CAP</p>
              <p>{fornitore.cap ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Comune</p>
              <p>{fornitore.comune ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Provincia</p>
              <p>{fornitore.provincia ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Nazione</p>
              <p>{fornitore.nazione ?? '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab catalogo / rappresentanti */}
      <Tabs defaultValue="catalogo">
        <TabsList>
          <TabsTrigger value="catalogo">
            Catalogo ({fornitore.catalogo_count})
          </TabsTrigger>
          <TabsTrigger value="rappresentanti">
            Rappresentanti ({fornitore.rappresentanti_count})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="catalogo" className="mt-4">
          <CatalogoTable fornitoreId={fornitore.id} />
        </TabsContent>
        <TabsContent value="rappresentanti" className="mt-4">
          <RappresentantiTable fornitoreId={fornitore.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FornitoreDetail;
